import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useDeviceFingerprint } from './useDeviceFingerprint';

const FREE_TRIAL_KEY = 'tree_rated_free_trial_used';

export const useFreeTrial = () => {
  const { getFingerprint } = useDeviceFingerprint();
  const [checking, setChecking] = useState(false);

  const hasUsedFreeTrial = (): boolean => {
    return localStorage.getItem(FREE_TRIAL_KEY) === 'true';
  };

  const markFreeTrialAsUsed = async (): Promise<void> => {
    // Mark in localStorage for quick local checks
    localStorage.setItem(FREE_TRIAL_KEY, 'true');
    
    try {
      // Get device fingerprint and register it in the backend
      const fingerprint = await getFingerprint();
      
      const { error } = await supabase.functions.invoke('register-free-trial', {
        body: { fingerprint }
      });

      if (error) {
        console.error('Error registering free trial:', error);
      }
    } catch (error) {
      console.error('Error marking free trial as used:', error);
    }
  };

  const canUseFreeTrial = async (): Promise<boolean> => {
    // Quick local check first
    if (hasUsedFreeTrial()) {
      return false;
    }

    setChecking(true);
    try {
      // Robust backend check using device fingerprint
      const fingerprint = await getFingerprint();
      
      const { data, error } = await supabase.functions.invoke('check-free-trial', {
        body: { fingerprint }
      });

      if (error) {
        console.error('Error checking free trial:', error);
        // If backend check fails, fall back to localStorage
        return !hasUsedFreeTrial();
      }

      return data?.canUse ?? false;
    } catch (error) {
      console.error('Error in canUseFreeTrial:', error);
      // If anything fails, fall back to localStorage
      return !hasUsedFreeTrial();
    } finally {
      setChecking(false);
    }
  };

  return {
    hasUsedFreeTrial,
    markFreeTrialAsUsed,
    canUseFreeTrial,
    checking,
  };
};
